/**
 * DemandTemplatesPage - Sprint 9
 * UI for managing tenant-specific demand templates
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  BookTemplate, Plus, Edit2, Trash2, RefreshCw, Check, X, Copy,
  Server, Code, Workflow, Shield, Users, FileText, BarChart3
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_ICONS = {
  infrastructure: Server,
  application: Code,
  process: Workflow,
  security: Shield,
  integration: Code,
  analytics: BarChart3,
  other: FileText
};

const CATEGORY_OPTIONS = [
  { value: 'infrastructure', label: 'Infrastructure' },
  { value: 'application', label: 'Application' },
  { value: 'process', label: 'Process' },
  { value: 'security', label: 'Security' },
  { value: 'integration', label: 'Integration' },
  { value: 'analytics', label: 'Analytics' },
  { value: 'other', label: 'Other' }
];

const DemandTemplatesPage = () => {
  const [templates, setTemplates] = useState([]);
  const [systemTemplates, setSystemTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'application',
    description_template: '',
    benefits_template: '',
    risks_template: '',
    defaults: {
      impact: 'medium',
      complexity: 3,
      urgency: 'medium'
    }
  });

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('simpliview_token');
    const tenantId = localStorage.getItem('impersonated_tenant_id') || localStorage.getItem('simpliview_user_tenant_id');
    const headers = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
    if (tenantId) {
      headers['X-Tenant-ID'] = tenantId;
    }
    return headers;
  }, []);

  const fetchTemplates = useCallback(async () => {
    try {
      const [tenantRes, systemRes] = await Promise.all([
        fetch(`${API_URL}/api/v2/demand-templates`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/v2/demands/tpl/list`, { headers: getAuthHeaders() })
      ]);

      if (tenantRes.ok) {
        const data = await tenantRes.json();
        setTemplates(data.data || []);
      }

      if (systemRes.ok) {
        const data = await systemRes.json();
        setSystemTemplates(data.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch templates:', err);
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleSubmit = async () => {
    if (!formData.name || !formData.description) {
      toast.error('Name and description are required');
      return;
    }

    setSaving(true);
    try {
      const url = editingTemplate
        ? `${API_URL}/api/v2/demand-templates/${editingTemplate.template_id}`
        : `${API_URL}/api/v2/demand-templates`;
      
      const method = editingTemplate ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(editingTemplate ? 'Template updated' : 'Template created');
        setShowModal(false);
        resetForm();
        fetchTemplates();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save template');
      }
    } catch (err) {
      toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('Delete this template?')) return;

    try {
      const response = await fetch(`${API_URL}/api/v2/demand-templates/${templateId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        toast.success('Template deleted');
        fetchTemplates();
      } else {
        toast.error('Failed to delete template');
      }
    } catch (err) {
      toast.error('Failed to delete template');
    }
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name || '',
      description: template.description || '',
      category: template.category || 'application',
      description_template: template.description_template || '',
      benefits_template: template.benefits_template || '',
      risks_template: template.risks_template || '',
      defaults: template.defaults || { impact: 'medium', complexity: 3, urgency: 'medium' }
    });
    setShowModal(true);
  };

  const handleCopySystem = (template) => {
    setEditingTemplate(null);
    setFormData({
      name: `${template.name} (Copy)`,
      description: template.description || '',
      category: template.category || 'application',
      description_template: '',
      benefits_template: '',
      risks_template: '',
      defaults: template.defaults || { impact: 'medium', complexity: 3, urgency: 'medium' }
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingTemplate(null);
    setFormData({
      name: '',
      description: '',
      category: 'application',
      description_template: '',
      benefits_template: '',
      risks_template: '',
      defaults: { impact: 'medium', complexity: 3, urgency: 'medium' }
    });
  };

  const CategoryIcon = ({ category }) => {
    const Icon = CATEGORY_ICONS[category] || FileText;
    return <Icon className="h-4 w-4" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="demand-templates-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <BookTemplate className="h-6 w-6" />
            Demand Templates
          </h1>
          <p className="text-gray-500 mt-1">
            Create and manage organization-specific demand templates
          </p>
        </div>
        <Button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="flex items-center gap-2"
          data-testid="create-template-btn"
        >
          <Plus className="h-4 w-4" />
          Create Template
        </Button>
      </div>

      {/* Organization Templates */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Users className="h-5 w-5" />
          Organization Templates
        </h2>
        
        {templates.length === 0 ? (
          <Card className="text-center py-8">
            <CardContent>
              <BookTemplate className="h-10 w-10 mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500">No custom templates yet</p>
              <p className="text-sm text-gray-400 mt-1">
                Create templates tailored to your organization's needs
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {templates.map((template) => (
              <Card key={template.template_id} className="hover:shadow-md transition-shadow">
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <div className="p-2 bg-indigo-100 rounded-lg">
                        <CategoryIcon category={template.category} />
                      </div>
                      <div>
                        <CardTitle className="text-base">{template.name}</CardTitle>
                        <Badge variant="outline" className="mt-1 text-xs">
                          {template.category}
                        </Badge>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(template)}
                        data-testid={`edit-${template.template_id}`}
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-500"
                        onClick={() => handleDelete(template.template_id)}
                        data-testid={`delete-${template.template_id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <p className="text-sm text-gray-600 line-clamp-2">
                    {template.description}
                  </p>
                  <div className="flex items-center justify-between mt-3 text-xs text-gray-500">
                    <span>Used {template.usage_count || 0} times</span>
                    {template.created_at && (
                      <span>Created {new Date(template.created_at).toLocaleDateString()}</span>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* System Templates */}
      <div>
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Shield className="h-5 w-5" />
          System Templates
          <Badge variant="secondary" className="ml-2">Read-only</Badge>
        </h2>
        
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {systemTemplates.map((template) => (
            <Card key={template.id} className="bg-gray-50/50">
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                  <div className="p-2 bg-gray-200 rounded-lg">
                    <CategoryIcon category={template.category} />
                  </div>
                  <div>
                    <CardTitle className="text-sm">{template.name}</CardTitle>
                    <Badge variant="outline" className="mt-1 text-xs">
                      {template.category}
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <p className="text-xs text-gray-600 line-clamp-2">
                  {template.description}
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-3 w-full"
                  onClick={() => handleCopySystem(template)}
                >
                  <Copy className="h-3 w-3 mr-2" />
                  Copy & Customize
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>
                  {editingTemplate ? 'Edit Template' : 'Create Template'}
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setShowModal(false)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <CardDescription>
                Configure a demand template for your organization
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 sm:col-span-1">
                  <label className="block text-sm font-medium mb-2">Name *</label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Template name"
                    data-testid="template-name-input"
                  />
                </div>
                <div className="col-span-2 sm:col-span-1">
                  <label className="block text-sm font-medium mb-2">Category</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    data-testid="template-category-select"
                  >
                    {CATEGORY_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Description *</label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Brief description of when to use this template"
                  rows={2}
                  data-testid="template-description-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Description Template</label>
                <Textarea
                  value={formData.description_template}
                  onChange={(e) => setFormData(prev => ({ ...prev, description_template: e.target.value }))}
                  placeholder="Pre-filled structure for the demand description (use ## for sections)"
                  rows={6}
                  className="font-mono text-sm"
                  data-testid="template-desc-template-input"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Use Markdown formatting. This will pre-fill the description field when using this template.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Benefits Template</label>
                  <Textarea
                    value={formData.benefits_template}
                    onChange={(e) => setFormData(prev => ({ ...prev, benefits_template: e.target.value }))}
                    placeholder="Expected benefits structure"
                    rows={4}
                    className="font-mono text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Risks Template</label>
                  <Textarea
                    value={formData.risks_template}
                    onChange={(e) => setFormData(prev => ({ ...prev, risks_template: e.target.value }))}
                    placeholder="Risk assessment structure"
                    rows={4}
                    className="font-mono text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Default Values</label>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs text-gray-500">Impact</label>
                    <select
                      value={formData.defaults?.impact || 'medium'}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        defaults: { ...prev.defaults, impact: e.target.value }
                      }))}
                      className="w-full px-2 py-1 text-sm border rounded"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Complexity (1-5)</label>
                    <select
                      value={formData.defaults?.complexity || 3}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        defaults: { ...prev.defaults, complexity: parseInt(e.target.value) }
                      }))}
                      className="w-full px-2 py-1 text-sm border rounded"
                    >
                      {[1, 2, 3, 4, 5].map(n => (
                        <option key={n} value={n}>{n}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Urgency</label>
                    <select
                      value={formData.defaults?.urgency || 'medium'}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        defaults: { ...prev.defaults, urgency: e.target.value }
                      }))}
                      className="w-full px-2 py-1 text-sm border rounded"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="critical">Critical</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setShowModal(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={saving}
                  data-testid="save-template-btn"
                >
                  {saving ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Check className="h-4 w-4 mr-2" />
                      {editingTemplate ? 'Update Template' : 'Create Template'}
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default DemandTemplatesPage;
